import { useEffect, useMemo, useRef } from 'react';
import type { AccommodationsConsoleState, AccommodationsScenario } from '@shared/types/Accommodations.types';
import { useAccommodationPlacesByCouple } from './useAccommodationPlacesByCouple';
import { useRegionalHotels } from './useRegionalHotels';
import { useBackgroundPricingRefresh } from './useBackgroundPricingRefresh';
import { useBatchedLivePricing } from './useBatchedLivePricing';
import { applyLivePricingToListings } from '../applyLivePricingToListings';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useListingsPanelTelemetry } from '../components/ListingsPanel/ListingsPanel.telemetry';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';
import type { FeaturedPlacementVariant } from './useFeaturedPlacements.types';
import { resolveMaxSponsoredSlots } from '../featuredPlacementSlots';

type UseAccommodationsConsoleListingsArgs = Readonly<{
  state: AccommodationsConsoleState;
  variant: 'couple' | 'regional';
  skipCouplePicks?: boolean;
  shouldFilterOutNonJoyRoomBlocks: boolean;
  eventHandle: Maybe<string>;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  isInitialLoad: boolean;
  /** Set only by the cash-fund variant; turns on each listing's server-computed fund/card split. */
  cashFund?: CashFundConfig;
  /**
   * Featured-placement A/B test — set for the `guest` scenario only, the one surface the treatments
   * render on. The variant decides how many sponsored slots the search asks for, so it has to be
   * resolved before the query fires (see AccommodationsConsole).
   */
  featuredPlacementVariant?: FeaturedPlacementVariant;
  /**
   * Which console surface this is. Decides the sponsored-slot request together with the variant —
   * see `resolveMaxSponsoredSlots`, which is the only place that choice is made — and is reported on
   * `destinationSearched` so fill rate can be read per surface.
   */
  scenario?: AccommodationsScenario;
  /** The page's own tags; decides the first request's fetch strategy before the URL is seeded. */
  defaultPrioritizeTags?: ReadonlyArray<string>;
}>;

export const useAccommodationsConsoleListings = (args: UseAccommodationsConsoleListingsArgs) => {
  const {
    state,
    variant,
    skipCouplePicks = false,
    shouldFilterOutNonJoyRoomBlocks,
    eventHandle,
    onError,
    onSuccess,
    isInitialLoad,
    cashFund,
    featuredPlacementVariant,
    scenario,
    defaultPrioritizeTags,
  } = args;

  const isViewingCouplesPicks = variant === 'couple' && !skipCouplePicks;

  // Scenario-aware on purpose: the two hotel-search consoles ask for what their arm says, and admin,
  // cashFund and honeymoon always ask for zero. `resolveMaxSponsoredSlots` owns that decision for
  // every surface. A caller that names no scenario omits the field, which the shipped contract
  // treats as zero — the same fail-closed answer, just not one we can state on its behalf.
  const maxSponsoredSlots = scenario === undefined ? undefined : resolveMaxSponsoredSlots(scenario, featuredPlacementVariant);

  const { eventAccommodations, isFetchingEventAccommodations, errorFetchingAccommodationPrices } = useAccommodationPlacesByCouple({
    state,
    eventHandle,
    skip: skipCouplePicks,
    shouldFilterOutNonJoyRoomBlocks,
  });

  const {
    payload,
    cachedListings,
    hasReceivedRegionalData,
    isFetchingListings,
    error: regionalError,
  } = useRegionalHotels({
    state,
    isInitialLoad,
    eventHandle,
    cashFund,
    maxSponsoredSlots,
    defaultPrioritizeTags,
  });

  // The cash-fund variant is never eligible: `JoyPlacePricingAndBookingDetails` carries no
  // `walletSpend`, and the split is computed against the rate it is shown with — a patched tile would
  // state a fund/card breakdown for the price it replaced. That scenario keeps the single refetch.
  // Skipped for cash fund so those viewers are never exposed to an experiment they cannot be in — an
  // exposure would put them in an arm and dilute it.
  const [isBatchedPricingEnabled, isBatchedPricingFlagLoading] = useIsFeatureEnabled('travelHotelSearchBatchedPricing', { skip: !!cashFund });
  const useBatchedPath = isBatchedPricingEnabled && !cashFund;

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  // Display order, so the first batch covers the tiles above the fold.
  const joyPlaceIds = useMemo(() => cachedListings.map(listing => listing.joyPlaceId).filter((id): id is string => !!id), [cachedListings]);

  const {
    pricingByJoyPlaceId,
    settledJoyPlaceIds,
    isSettling: isSettlingLivePrices,
    batchCount,
    batchSize,
    failedBatchCount,
    msToFirstBatch,
    msToAllBatches,
    fanOutStartedAt,
    canFetch: canBatchLivePricing,
    isPreparingPayload: isPreparingBatchedPayload,
  } = useBatchedLivePricing({
    joyPlaceIds,
    regionalHotelsPayload: payload,
    searchDates: state.searchDates,
    occupancies: state.occupancies,
    skip: !useBatchedPath || isFetchingListings || !hasReceivedRegionalData,
  });

  // Batching only actually runs when a pricing payload can be built. If it cannot, fall back to the
  // whole-list refetch rather than leave the user with snapshot prices and no live ones at all.
  const isBatchingLivePrices = useBatchedPath && canBatchLivePricing;

  // Hold the whole-list refetch while the choice between the two paths is still pending — the flag has
  // not resolved, or the batch payload is only waiting on search metadata. Firing it in that window
  // and then the batches as well would price every hotel twice. A failed flag fetch still resolves
  // (to control), so this cannot hold live prices back indefinitely.
  const isLivePricingPathPending = !cashFund && (isBatchedPricingFlagLoading || (useBatchedPath && !canBatchLivePricing && isPreparingBatchedPayload));

  const {
    error: backgroundError,
    listings: backgroundListings,
    isLoading: isBackgroundPricingLoading,
  } = useBackgroundPricingRefresh({
    isFetchingListings,
    hasReceivedRegionalData,
    regionalHotelsPayload: payload,
    locationName: state.searchLocation,
    cashFund,
    featuredPlacementVariant,
    scenario,
    skip: isBatchingLivePrices || isLivePricingPathPending,
  });

  const pricedRegionalListings = useMemo(
    () =>
      isBatchingLivePrices
        ? applyLivePricingToListings({ listings: cachedListings, pricingByJoyPlaceId, settledJoyPlaceIds, isSettling: isSettlingLivePrices, mapHotelRoomPrice })
        : cachedListings,
    [isBatchingLivePrices, cachedListings, pricingByJoyPlaceId, settledJoyPlaceIds, isSettlingLivePrices, mapHotelRoomPrice],
  );

  // `destinationSearched` is the console's search-latency event, and it lives in the refetch hook this
  // path skips — so fire it here instead, once, when the last batch settles. Per batch would multiply
  // it ~8x and break every dashboard reading it. `livePricingSettled` carries the experiment readout.
  const telemetry = useListingsPanelTelemetry();

  // Started when the search payload changes — the same moment `useBackgroundPricingRefresh` starts its
  // clock — so both arms' latencies include the snapshot search. The fan-out only begins once that
  // search has settled; timing from there would credit treatment with the snapshot's whole wait.
  const searchStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    searchStartedAtRef.current = Date.now();
  }, [payload]);

  // Keyed on the fan-out, not its duration: two searches can settle in the same number of ms.
  const reportedFanOutRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isBatchingLivePrices || msToAllBatches === null || fanOutStartedAt === null || reportedFanOutRef.current === fanOutStartedAt) {
      return;
    }
    reportedFanOutRef.current = fanOutStartedAt;
    // Every batch failed: the refetch path sends no `destinationSearched` when its one request fails, so
    // neither does this one — otherwise the arms would differ in which searches reach the latency read.
    // `livePricingSettled` still reports it, with the failure count.
    const didEveryBatchFail = batchCount > 0 && failedBatchCount >= batchCount;
    const searchStartedAt = searchStartedAtRef.current ?? fanOutStartedAt;
    const googlePlaceId = payload?.customSearchParams?.location?.context?.googlePlaceId;
    if (!didEveryBatchFail) {
      telemetry.destinationSearched({
        googlePlaceId,
        locationName: state.searchLocation,
        responseTimeMs: fanOutStartedAt + msToAllBatches - searchStartedAt,
        msToFirstLivePrice: msToFirstBatch === null ? undefined : fanOutStartedAt + msToFirstBatch - searchStartedAt,
        livePricingPath: 'batched',
        // Same meaning as the refetch path: hotels the search returned, not hotels that got a live price.
        // The priced count is on `livePricingSettled`.
        resultCount: cachedListings.length,
        // Sponsored inventory is decided by the snapshot search; pricing by id never adds or drops a
        // sponsored hotel, so the fill rate is read off the list the batches patch.
        sponsoredSlotsRequested: payload?.customSearchParams?.maxSponsoredSlots ?? undefined,
        sponsoredResultCount: cachedListings.filter(listing => listing.isSponsored === true).length,
        featuredPlacementVariant,
        scenario,
      });
    }
    telemetry.livePricingSettled({
      batchCount,
      batchSize,
      joyPlaceIdCount: joyPlaceIds.length,
      pricedCount: pricingByJoyPlaceId.size,
      failedBatchCount,
      msToFirstBatch,
      msToAllBatches,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per settled fan-out; the rest are read-at-fire context
  }, [isBatchingLivePrices, msToAllBatches, fanOutStartedAt]);

  const hasAnyError = Boolean(regionalError || backgroundError);
  // Only treat primary regional listing errors as API errors that should show error state
  // Background pricing refresh failures are non-critical and shouldn't trigger error UI
  const hasApiError = Boolean(regionalError);
  const areQueriesSettled = !isFetchingListings && !isBackgroundPricingLoading;
  const shouldUseBackgroundListings = areQueriesSettled && !backgroundError && backgroundListings.length > 0;

  const consolidatedListings = useMemo(() => {
    // Batched path: one list throughout, with live prices patched in as each batch lands. Control path
    // keeps the all-or-nothing swap — the realtime refetch replaces the snapshot list once it settles.
    const regionalListings = isBatchingLivePrices ? pricedRegionalListings : shouldUseBackgroundListings ? backgroundListings : cachedListings;

    if (skipCouplePicks) {
      return regionalListings;
    }

    const searchScoreByJoyPlaceId = new Map(regionalListings.filter(l => l.joyPlaceId).map(l => [l.joyPlaceId, l.searchScore]));

    const couplePickJoyPlaceIds = new Set<string>(eventAccommodations.filter(x => x.joyPlaceId).map(x => x.joyPlaceId) as string[]);

    const regionalListingsJoyPlaceIds = new Set<string>(regionalListings.filter(l => l.joyPlaceId).map(l => l.joyPlaceId as string));
    const couplesPickToShow = isInitialLoad
      ? eventAccommodations
      : eventAccommodations.filter(accommodation => accommodation.joyPlaceId && regionalListingsJoyPlaceIds.has(accommodation.joyPlaceId));

    return [
      ...couplesPickToShow.map(accommodation => {
        const matchingRegionalListing = regionalListings.find(listing => listing.joyPlaceId === accommodation.joyPlaceId);
        // The refresh (prefer-vendor) can omit a hotel the first paint (snapshots) had. Its tags came
        // from the regional search, so without this fallback a tagged couple's pick would lose them
        // when the refresh settles and be filtered out of a tag-filtered page it belonged on.
        const firstPaintListing = matchingRegionalListing ?? cachedListings.find(listing => listing.joyPlaceId === accommodation.joyPlaceId);
        const updates = {
          searchScore: accommodation.joyPlaceId ? searchScoreByJoyPlaceId.get(accommodation.joyPlaceId) : undefined,
          otaProvider: matchingRegionalListing?.otaProvider,
          // Prefer couple pick's own chain (from secondary fetch), fall back to regional listing
          chain: accommodation.chain ?? matchingRegionalListing?.chain ?? null,
          // Carried across, because only the regional search asks for tags: the couple's-picks query
          // does not request them, so substituting its DTO here would drop `tags` and a tagged hotel
          // that is ALSO a couple's pick would silently be filtered out (useListingSort matches on
          // this field). Own value first for when that query does start returning them.
          tags: accommodation.tags ?? firstPaintListing?.tags,
        };
        // Regional results never include room blocks, and the fund cannot pay for one anyway.
        if (accommodation.type !== 'standardHotel') {
          return { ...accommodation, ...updates };
        }
        // Only the regional search selects the fund/card split, and the server computed it against
        // that tile's rate. A matched pick borrows both together: its own price under a split for
        // another total would not add up.
        if (!accommodation.isDirectBooking && matchingRegionalListing?.walletSpend && matchingRegionalListing.pricingInfo.values) {
          return { ...accommodation, ...updates, pricingInfo: matchingRegionalListing.pricingInfo, walletSpend: matchingRegionalListing.walletSpend };
        }
        // The dedicated per-place couple's-pick pricing query (useAccommodationPlacesPricing)
        // averages 2.7s and p95 7.6s — long enough that users see a price skeleton on the
        // featured "Couple's Pick" tile while regional tiles already show prices. When the
        // same hotel appears in the regional listings, its pricing has already loaded; surface
        // it immediately and let the per-place query overwrite when it lands.
        // `!isDirectBooking` on both borrow branches: a direct-booking pick's rate was deliberately
        // blanked, so borrowing would put the OTA price back on a tile that must show none. The other
        // side needs no guard — a direct-booking regional row was blanked by
        // `transformPropertiesToListings`, so its `pricingInfo.values` is null and there is nothing
        // to borrow.
        if (!accommodation.isDirectBooking && !accommodation.pricingInfo.values && matchingRegionalListing?.pricingInfo.values) {
          return { ...accommodation, ...updates, pricingInfo: matchingRegionalListing.pricingInfo, walletSpend: null };
        }
        return { ...accommodation, ...updates, walletSpend: null };
      }),
      ...regionalListings.filter(listing => !!listing.joyPlaceId && !couplePickJoyPlaceIds.has(listing.joyPlaceId)),
    ];
  }, [eventAccommodations, backgroundListings, cachedListings, skipCouplePicks, isInitialLoad, shouldUseBackgroundListings, isBatchingLivePrices, pricedRegionalListings]);

  // Notify parent of success/error states
  // Using stable callbacks to prevent infinite loops when error persists
  useEffect(() => {
    if (regionalError) {
      onError?.(regionalError);
    } else if (areQueriesSettled && cachedListings.length > 0) {
      onSuccess?.();
    }
  }, [regionalError, areQueriesSettled, cachedListings.length, onError, onSuccess]);

  return {
    cachedListings,
    listings: consolidatedListings,
    hasAccommodationPlacesByCouple: eventAccommodations.length > 0,
    isFetchingListings: isFetchingListings || isFetchingEventAccommodations,
    // "no usable price exists yet". Deliberately NOT `isSettlingLivePrices`: this flag blanks the whole
    // price facet (PriceRangeFacet) and disables the user's price filter (useListingFilters), which is
    // wrong while snapshot prices are already on screen and readable.
    // Batched path only. The refetch path has always reported `false` here, and control must stay
    // byte-for-byte the legacy behaviour (price facet skeleton, URL price filter) for a clean baseline.
    isFetchingListingPrices: isBatchingLivePrices && (isFetchingListings || (cachedListings.length === 0 && isSettlingLivePrices)),
    /** Live prices are still streaming in. For consumers that must hold steady while they land —
     *  the price-slider bound and the pagination scroll reset — not for hiding readable prices. */
    isRefreshingListingPrices: isSettlingLivePrices,
    hasAnyError,
    hasApiError,
    isCombinedLoading: !areQueriesSettled,
    /** Renamed from `isUsingBackgroundListings`: under the batched path there is no single moment when
     *  the realtime list takes over. The only consumer gates map-viewport sorting, which reads
     *  `location` and `searchScore` — both from the snapshot search and never touched by a price patch. */
    hasSettledListings: isBatchingLivePrices ? hasReceivedRegionalData && !isFetchingListings : shouldUseBackgroundListings,
    pricingError: isViewingCouplesPicks ? errorFetchingAccommodationPrices : null,
  };
};
